import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PortalPageHeader } from "@/components/portal/PortalLayout";
import { toast } from "sonner";
import { Car, Wallet, Receipt, AlertTriangle, CheckCircle2, Circle } from "lucide-react";

export default function OwnerDashboard() {
  const [me, setMe] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [m, v, p, e] = await Promise.all([
          api.get("/owner/me"),
          api.get("/owner/vehicles"),
          api.get("/owner/payments"),
          api.get("/owner/expenses"),
        ]);
        setMe(m.data); setVehicles(v.data); setPayments(p.data); setExpenses(e.data);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  if (!me) return <div className="p-10 text-slate-500">A carregar…</div>;

  const paymentsPending = payments.filter((p) => p.status === "pending");
  const paymentsPaid = payments.filter((p) => p.status === "paid");
  const totalPending = paymentsPending.reduce((a, b) => a + (b.amount_eur || 0), 0);
  const totalPaid = paymentsPaid.reduce((a, b) => a + (b.amount_eur || 0), 0);
  const expensesPending = expenses.filter((e) => e.status === "pending");
  const totalDebt = expensesPending.reduce((a, b) => a + (b.amount_eur || 0), 0);

  return (
    <div className="p-10 max-w-[1400px]" data-testid="owner-dashboard">
      <PortalPageHeader eyebrow={`Olá, ${me.name}`} title="Painel do Dono" description="Os seus veículos alugados, pagamentos e despesas registadas." />

      <div className="grid grid-cols-4 gap-4 mb-8">
        <KPI label="Veículos" value={vehicles.length} icon={Car} testId="kpi-vehicles" />
        <KPI label="A receber" value={`€ ${totalPending.toFixed(2)}`} icon={Wallet} gold testId="kpi-pending" />
        <KPI label="Recebido" value={`€ ${totalPaid.toFixed(2)}`} icon={CheckCircle2} testId="kpi-paid" />
        <KPI label="Dívida pendente" value={`€ ${totalDebt.toFixed(2)}`} icon={Receipt} warning={totalDebt > 0} testId="kpi-debt" />
      </div>

      <section className="mb-10">
        <h2 className="font-display text-xl text-[#0a0f1e] mb-4">Os meus veículos</h2>
        {vehicles.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Ainda não tem veículos com contrato activo.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {vehicles.map((v) => (
              <div key={v.id} className="border border-slate-200 bg-white p-4" data-testid={`owner-vehicle-${v.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-sm text-[#0a0f1e] font-bold">{v.plate}</div>
                    <div className="text-xs text-slate-600">{v.brand} {v.model} {v.year}</div>
                  </div>
                  {v.current_contract && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Semanal</div>
                      <div className="text-[#f59e0b] font-mono font-bold">€ {v.current_contract.weekly_fee_eur}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mb-10">
        <h2 className="font-display text-xl text-[#0a0f1e] mb-4">Pagamentos semanais</h2>
        {payments.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Sem pagamentos ainda.</div>
        ) : (
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600 font-bold">
                <tr>
                  <th className="text-left px-3 py-2">Semana</th>
                  <th className="text-right px-3 py-2">Valor</th>
                  <th className="text-center px-3 py-2">Estado</th>
                  <th className="text-left px-3 py-2">Pago em</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`owner-payment-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.week_start} → {p.week_end}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">€ {p.amount_eur.toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      {p.status === "paid"
                        ? <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] uppercase tracking-widest font-bold"><CheckCircle2 className="w-3 h-3" /> Pago</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700 text-[10px] uppercase tracking-widest font-bold"><Circle className="w-3 h-3" /> Pendente</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{p.paid_at ? new Date(p.paid_at).toLocaleDateString("pt-PT") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-xl text-[#0a0f1e] mb-4">Despesas do veículo (dívida)</h2>
        {expenses.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Sem despesas registadas.</div>
        ) : (
          <div className="space-y-2">
            {expenses.map((e) => (
              <div key={e.id} className="border border-slate-200 bg-white p-3 flex items-center gap-4" data-testid={`owner-expense-${e.id}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#0a0f1e] capitalize">{e.category} · {e.description || "—"}</div>
                  <div className="text-[11px] text-slate-500">{e.incurred_at} · pago por <strong>{e.paid_by === "owner" ? "Dono" : "Parceiro"}</strong></div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-amber-700">€ {e.amount_eur.toFixed(2)}</div>
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">{e.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function KPI({ label, value, icon: Icon, gold, warning, testId }) {
  return (
    <div className={`border p-4 ${warning ? "border-amber-300 bg-amber-50/60" : "border-slate-200 bg-white"}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`mt-1 font-display text-2xl ${gold ? "text-[#f59e0b] font-black" : warning ? "text-amber-700 font-black" : "text-[#0a0f1e]"}`}>{value}</div>
    </div>
  );
}
